<script>
import confirmationModal from "../../components/modals/modal-confirmation.vue";

const EVENT_OPTIONS = [
  { value: 3, label: "Proof created" },
  { value: 4, label: "Proof sent to customer" },
  { value: 7, label: "Approved" },
  { value: 6, label: "Approved with changes" },
  { value: 8, label: "Not approved / changes requested" },
  { value: 39, label: "Approver responded (with comments)" },
  { value: 34, label: "New version created" },
  { value: 45, label: "Annotation added" },
  { value: 60, label: "Proof files ready" },
  { value: 61, label: "New version files ready" },
];

export default {
  name: "webhookManager",
  components: {
    confirmationModal,
  },
  data() {
    return {
      loaded: false,
      hooks: [],
      eventOptions: EVENT_OPTIONS,
      formOpen: false,
      editingId: 0,
      form: this.emptyForm(),
      saving: false,
      toDelete: { id: 0, name: "" },
    };
  },
  mounted: async function () {
    await this.load();
  },
  methods: {
    emptyForm() {
      return {
        name: "",
        url: "",
        signingSecret: "",
        allEvents: false,
        events: [4, 6, 7, 8, 34, 39],
      };
    },
    load: async function () {
      try {
        const health = await this.$A.IntegrationService.GetWebHookHealth();
        if (health && Array.isArray(health.data)) {
          this.hooks = health.data;
        }
      } catch (e) {
        this.hooks = [];
      }
      this.loaded = true;
    },
    openAdd() {
      this.editingId = 0;
      this.form = this.emptyForm();
      this.formOpen = true;
    },
    async openEdit(hook) {
      try {
        const response = await this.$A.IntegrationService.GetWebHook(hook.webHookId);
        const full = response.data || {};
        this.editingId = hook.webHookId;
        this.form = {
          name: full.name || hook.name,
          url: full.url || hook.url,
          signingSecret: full.signingSecret || "",
          allEvents: Array.isArray(full.eventTypeGroupsToSend) && full.eventTypeGroupsToSend.indexOf(1) !== -1,
          events: Array.isArray(full.eventTypesToSend) ? full.eventTypesToSend.slice() : [],
        };
        this.formOpen = true;
      } catch (e) {
        this.$A.AlertUser("Could not load that webhook.", "warning");
      }
    },
    async save() {
      if (this.saving) {
        return;
      }
      if (!this.form.name || !this.form.url) {
        this.$A.AlertUser("A name and an HTTPS URL are required.", "warning");
        return;
      }
      if (!this.form.allEvents && this.form.events.length === 0) {
        this.$A.AlertUser("Pick at least one event, or choose all activity.", "warning");
        return;
      }
      this.saving = true;
      const request = {
        name: this.form.name,
        url: this.form.url,
        active: true,
        eventTypesToSend: this.form.allEvents ? [] : this.form.events,
        eventTypeGroupsToSend: this.form.allEvents ? [1] : [],
      };
      if (this.form.signingSecret) {
        request.signingSecret = this.form.signingSecret;
      }
      try {
        if (this.editingId > 0) {
          await this.$A.IntegrationService.UpdateWebHook(this.editingId, request);
        } else {
          await this.$A.IntegrationService.SaveWebHook(request);
        }
        this.$A.AlertUser("Webhook Saved", "success");
        this.formOpen = false;
        await this.load();
      } catch (e) {
        const message = _.get(e, "response.data.message", "Could not save the webhook.");
        this.$A.AlertUser(message, "warning");
      }
      this.saving = false;
    },
    async toggleActive(hook) {
      try {
        const response = await this.$A.IntegrationService.GetWebHook(hook.webHookId);
        const full = response.data || {};
        await this.$A.IntegrationService.UpdateWebHook(hook.webHookId, {
          name: full.name,
          url: full.url,
          active: !full.active,
          signingSecret: full.signingSecret,
          eventTypesToSend: full.eventTypesToSend || [],
          eventTypeGroupsToSend: full.eventTypeGroupsToSend || [],
        });
        await this.load();
      } catch (e) {
        this.$A.AlertUser("Could not update the webhook.", "warning");
      }
    },
    confirmDelete(hook) {
      this.toDelete = { id: hook.webHookId, name: hook.name || hook.url };
      this.$bvModal.show("deleteManagedWebHook");
    },
    async actuallyDelete() {
      if (this.toDelete.id > 0) {
        try {
          await this.$A.IntegrationService.DeleteWebHook(this.toDelete.id);
          this.$A.AlertUser("Webhook Deleted", "success");
        } catch (e) {
          this.$A.AlertUser("Could not delete the webhook.", "warning");
        }
        await this.load();
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "—";
    },
  },
};
</script>

<template>
  <div class="settings-card">
    <div class="card-head manager-head">
      <div>
        <div class="card-head-title">Webhooks</div>
        <div class="card-head-subtitle">
          Where Ashore sends proof events — approvals, comments, new versions —
          as they happen. No code or API calls needed.
        </div>
      </div>
      <a class="primary-btn" @click="openAdd">Add Webhook</a>
    </div>

    <div v-if="loaded && hooks.length === 0 && !formOpen" class="list-empty">
      <div class="empty-title">No webhooks yet</div>
      <div class="empty-text">
        Add one to push proof events to Zoho Flow, Slack, or any HTTPS endpoint —
        integrations like Zoho register theirs automatically when they connect.
      </div>
      <a class="primary-btn empty-cta" @click="openAdd">Add Webhook</a>
    </div>

    <div class="hook-rows" v-if="hooks.length > 0">
      <div class="hook-row hook-row-head">
        <span class="hook-col-name">Webhook</span>
        <span class="hook-col-num">Delivered 7d</span>
        <span class="hook-col-num">Failed 7d</span>
        <span class="hook-col-time">Last delivery</span>
        <span class="hook-col-actions"></span>
      </div>
      <div class="hook-row" v-for="hook in hooks" :key="hook.webHookId">
        <span class="hook-col-name">
          <span class="hook-dot" :class="hook.active ? 'hook-dot-on' : 'hook-dot-off'"></span>
          <span class="hook-name-text">
            <b>{{ hook.name || "(unnamed)" }}</b>
            <span class="hook-url">{{ hook.url }}</span>
          </span>
        </span>
        <span class="hook-col-num">{{ hook.deliveredLast7Days }}</span>
        <span class="hook-col-num" :class="{ 'hook-failed': hook.failedLast7Days > 0 }">{{ hook.failedLast7Days }}</span>
        <span class="hook-col-time">{{ formatDate(hook.lastDeliveredAt) }}</span>
        <span class="hook-col-actions">
          <a class="action-link" @click="openEdit(hook)">Edit</a>
          <a class="action-link" @click="toggleActive(hook)">{{ hook.active ? "Disable" : "Enable" }}</a>
          <a class="action-link action-danger" @click="confirmDelete(hook)">Delete</a>
        </span>
      </div>
    </div>

    <div class="card-body" v-if="formOpen">
      <div class="field">
        <label class="field-label" for="whName">Name</label>
        <p class="field-help">Saving with an existing name updates that webhook.</p>
        <input id="whName" type="text" class="text-input" v-model="form.name" :disabled="editingId > 0" />
      </div>
      <div class="field">
        <label class="field-label" for="whUrl">Destination URL</label>
        <p class="field-help">HTTPS endpoint that receives the events — a Zoho Flow webhook URL, a Deluge REST function, anything that accepts JSON.</p>
        <input id="whUrl" type="text" class="text-input" v-model="form.url" placeholder="https://" />
      </div>
      <div class="field">
        <label class="field-label">Events to send</label>
        <div class="ashore-toggle">
          <label class="switch">
            <input type="checkbox" v-model="form.allEvents" />
            <span class="slider round"></span>
          </label>
          All activity
        </div>
        <div class="event-grid" v-show="!form.allEvents">
          <label class="event-option" v-for="option in eventOptions" :key="option.value">
            <input type="checkbox" :value="option.value" v-model="form.events" />
            {{ option.label }}
          </label>
        </div>
      </div>
      <div class="field">
        <label class="field-label" for="whSecret">Signing secret <span class="optional-tag">optional</span></label>
        <p class="field-help">When set, every delivery carries an HMAC signature header so your endpoint can verify it came from Ashore.</p>
        <input id="whSecret" type="text" class="text-input" v-model="form.signingSecret" />
      </div>
    </div>

    <confirmationModal
        v-on:confirmed="actuallyDelete"
        ref="confirmDeleteHook"
        name="deleteManagedWebHook"
        confirm-button-text="Delete Webhook"
        question="Are you sure you would like to delete this webhook?"
        :body="'Events will stop delivering to it immediately. <br/><br/> <b>Deleting:</b> ' + toDelete.name"
    />

    <div class="card-foot" v-if="formOpen">
      <a class="secondary-btn" @click="formOpen = false">Cancel</a>
      <a class="primary-btn" @click="save">{{ editingId > 0 ? "Save Changes" : "Create Webhook" }}</a>
    </div>
  </div>
</template>

<style scoped>
.settings-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
}
.manager-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.card-head-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.card-head-subtitle {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
}
.card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.card-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 0 0 12px 12px;
}
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.optional-tag {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #9ca3af;
}
.field-help {
  margin: -2px 0 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.text-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13.5px;
  color: #111827;
  background: #fff;
}
.text-input:focus {
  outline: none;
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.text-input:disabled {
  background: #f3f4f6;
  color: #6b7280;
}
.event-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 16px;
  margin-top: 10px;
}
.event-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  margin: 0;
}
.event-option input {
  accent-color: #019256;
}
/* rows */
.hook-rows {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.hook-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.hook-row:last-child {
  border-bottom: 0;
}
.hook-row-head {
  padding: 8px 20px;
  background: #fbfbfc;
}
.hook-row-head span {
  font-size: 10.5px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.hook-col-name {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.hook-name-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: #111827;
}
.hook-url {
  font-size: 11.5px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hook-col-num {
  width: 90px;
  flex-shrink: 0;
  font-size: 12.5px;
  color: #4b5563;
  text-align: right;
}
.hook-failed {
  color: #b43643;
  font-weight: 700;
}
.hook-col-time {
  width: 160px;
  flex-shrink: 0;
  font-size: 12px;
  color: #6b7280;
  text-align: right;
}
.hook-col-actions {
  width: 160px;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.hook-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.hook-dot-on {
  background: #019256;
}
.hook-dot-off {
  background: #9ca3af;
}
.action-link {
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  color: #019256;
  text-decoration: none;
}
.action-link:hover {
  color: #017a48;
  text-decoration: none;
}
.action-danger {
  color: #b43643;
}
.action-danger:hover {
  color: #8f2a35;
}
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 32px;
}
.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.empty-text {
  margin-top: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.55;
  max-width: 420px;
}
.empty-cta {
  margin-top: 16px;
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 120ms ease;
  flex-shrink: 0;
}
.primary-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}
.secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  background: #fff !important;
  color: #1f2937 !important;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.secondary-btn:hover {
  border-color: #019256 !important;
  color: #019256 !important;
  text-decoration: none;
}
@media (max-width: 767px) {
  .hook-col-time {
    display: none;
  }
  .hook-col-num {
    width: 60px;
  }
}
</style>
